import css from 'styled-jsx/css'
import { SyncedProp } from '@ui/hooks'
import { useEffect } from 'react'
import { logger } from '@ui/analytics'
import { iconPath } from '@ui/helpers'

interface QuantityInputProps extends React.HTMLAttributes<HTMLDivElement> {
  label: string
  isCta?: boolean
  isHorizontal?: boolean
  valueSyncedProp: SyncedProp<number>
  emitChange?: boolean
}

export default function QuantityInput({
  label = 'Quantity',
  isCta = true,
  isHorizontal = true,
  valueSyncedProp,
  emitChange = false,
  className
}: QuantityInputProps) {
  const [syncedValue, setSyncedValue] = valueSyncedProp

  useEffect(() => {
    emitChange && logger.logCart('quantity')
  }, [syncedValue])

  const onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === '.') {
      event.preventDefault()
    }
  }

  const onChange = (event: any) => {
    setSyncedValue(parseInt(event.target.value, 10) || 1)
  }

  function increment() {
    setSyncedValue(syncedValue + 1)
  }

  function decrement() {
    setSyncedValue(Math.max(syncedValue - 1, 1))
  }
  return (
    <div
      className={`quantity-input field ${isHorizontal ? 'is-horizontal' : ''} ${
        isCta ? 'is-cta' : ''
      } ${className ?? ''}`}
    >
      <style jsx global>
        {globalStyle}
      </style>
      {label && (
        <div className="is-hidden-tablet field-label is-normal">
          <label className="label is-capitalized has-text-left label-quantity">{label}</label>
        </div>
      )}
      <div className="field-body">
        <div className="field has-addons">
          <p className="control">
            <button className="icon-button button left" aria-label="decrement" onClick={decrement}>
              <img src={iconPath('minus-mobile.svg')} alt="" />
            </button>
          </p>
          <p className="control control-input">
            <input
              value={syncedValue}
              type="tel"
              aria-label="quantity"
              className="input"
              onKeyPress={onKeyPress}
              onChange={onChange}
            />
          </p>
          <p className="control is-right">
            <button className="icon-button button right" aria-label="increment" onClick={increment}>
              <img src={iconPath('plus-mobile.svg')} alt="" />
            </button>
          </p>
        </div>
      </div>
    </div>
  )
}

const globalStyle = css.global`
  .quantity-input {
    padding-bottom: 8px;
    .icon-button {
      img {
        width: 16px;
        height: 16px;
      }
    }
    .is-right {
      margin-left: 1px;
    }

    .button {
      height: 48px;
      width: 48px;
      border-radius: 6px;
      border: 1px solid var(--indigo-5);
      &:focus,
      &:hover {
        box-shadow: none;
      }

      &.left {
        margin-right: 1px;
        border-right: none;
      }

      &.right {
        border-left: none;
      }
    }

    .input {
      border-right: 0;
      border-left: 0;
      box-shadow: none;
      height: 36px;
      padding-right: 0 !important;
      padding-left: 0 !important;
      text-align: center;
      width: 2rem;

      &:focus,
      &:hover {
        box-shadow: none;
        border-color: #dbdbdb;
      }

      &[type='number'] {
        -webkit-appearance: textfield;
        -moz-appearance: textfield;
        appearance: textfield;
      }
      &[type='number']::-webkit-inner-spin-button,
      &[type='number']::-webkit-outer-spin-button {
        -webkit-appearance: none;
      }
    }

    &.is-horizontal {
      align-items: center;

      .button,
      .input {
        height: 40px;
      }

      .button {
        padding: 5px 12px;
      }
    }

    &.is-cta {
      .button,
      .input {
        font-weight: 400;
        font-size: 18px;
        line-height: 30px;
        height: 56px;
        padding: 15px 12px;
      }
    }

    &.is-fullwidth {
      .control-input {
        width: calc(100% - 60px);

        .input {
          width: 100%;
        }
      }
    }

    &.is-fullwidth-mobile {
      @media screen and (max-width: 768px) {
        .button {
          &.left {
            border-right: 1px solid var(--indigo-5);
          }

          &.right {
            border-left: 1px solid var(--indigo-5);
          }
        }
        .control-input {
          width: calc(100% - 60px);

          .input {
            width: 100%;
            font-weight: 600;
            font-size: 18px;
            line-height: 28px;
          }
        }
      }
    }
  }

  @media screen and (max-width: 768px) {
    .quantity-input {
      padding-bottom: 2px;
    }
  }

  .field-label .label-quantity {
    font-weight: 600;
    font-size: 14px;
    line-height: 24px;
  }
`
